import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Trash2, Sparkles, Edit3, Save, Check, X } from "lucide-react";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "Ready to send" },
  { key: "sent", label: "Sent" },
  { key: "sent_failed", label: "Failed" },
];

export default function Outbox() {
  const [drafts, setDrafts] = useState([]);
  const [status, setStatus] = useState("draft");
  const [editing, setEditing] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/drafts", { params: { status_filter: status } });
      setDrafts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openEdit = (d) => { setEditing(d); setSubject(d.subject || ""); setBody(d.body || ""); };
  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/drafts/${editing.id}`, { subject, body });
      setDrafts((prev) => prev.map((d) => d.id === data.id ? data : d));
      setEditing(null);
      toast.success("Draft updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendDraft = async (d) => {
    if (!confirm(`Send this email to ${d.contact_email}?`)) return;
    setBusy(true);
    try {
      await api.post(`/drafts/${d.id}/send`);
      toast.success("Email sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!confirm("Delete this draft?")) return;
    try {
      await api.delete(`/drafts/${d.id}`);
      setDrafts((prev) => prev.filter((x) => x.id !== d.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="outbox-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Approval queue</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Outbox</h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm">AI-drafted emails waiting for your one-click approval.</p>
        </div>
        <div className="flex items-center gap-1 border border-[var(--peak-border)] bg-white">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key || "all"}
              data-testid={`outbox-filter-${s.key || "all"}`}
              onClick={() => setStatus(s.key)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                status === s.key ? "bg-[var(--peak-ink)] text-white" : "hover:bg-[var(--peak-surface-alt)]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="drafts-list">
        {drafts.length === 0 && (
          <div className="col-span-full brutalist-card p-10 text-center">
            <Sparkles className="w-6 h-6 mx-auto text-[var(--peak-blue-deep)]" />
            <div className="font-heading font-bold text-2xl mt-2">Nothing here yet</div>
            <div className="section-label mt-1">Go to any contact and click "AI draft email" to stage one.</div>
            <button onClick={() => navigate("/contacts")} className="mt-4 border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2">Go to contacts</button>
          </div>
        )}
        {drafts.map((d) => (
          <div key={d.id} className="brutalist-card p-6" data-testid={`draft-${d.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="section-label">
                  {d.status === "sent" ? <span className="text-[#008A27]">SENT</span> :
                   d.status === "sent_failed" ? <span className="text-[#E50000]">FAILED</span> :
                   <span>DRAFT</span>}
                  {d.generated_by_ai && <span className="ml-2 text-[var(--peak-blue-deep)]">· AI</span>}
                  {" · "}
                  <span className="normal-case">to {d.contact_name || d.contact_email}</span>
                </div>
                <Link to={`/contacts/${d.contact_id}`} className="text-xs text-[var(--peak-muted)] hover:text-[var(--peak-accent)] font-mono">{d.contact_email}</Link>
              </div>
              <button onClick={() => remove(d)} data-testid={`del-draft-${d.id}`} className="hover:text-[#E50000]"><Trash2 className="w-4 h-4" /></button>
            </div>

            {editing?.id === d.id ? (
              <>
                <input
                  data-testid={`edit-subject-${d.id}`}
                  className="brutalist-input font-heading font-bold text-lg"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <textarea
                  data-testid={`edit-body-${d.id}`}
                  rows={8}
                  className="w-full border border-[var(--peak-border)] p-2 text-sm mt-2 focus:outline-none focus:border-[var(--peak-ink)] font-mono"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                <div className="mt-3 flex gap-2">
                  <button onClick={saveEdit} disabled={busy} data-testid={`save-edit-${d.id}`} className="bg-[var(--peak-ink)] text-white hover:bg-[var(--peak-accent)] font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"><Save className="w-3 h-3" /> Save edit</button>
                  <button onClick={() => setEditing(null)} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="font-heading font-bold text-lg tracking-tight leading-tight" data-testid={`draft-subject-${d.id}`}>{d.subject}</div>
                <pre className="mt-2 whitespace-pre-wrap text-sm font-body text-[var(--peak-ink)] max-h-40 overflow-y-auto tick-scroll" data-testid={`draft-body-${d.id}`}>{d.body}</pre>
                {d.status === "draft" && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button onClick={() => sendDraft(d)} disabled={busy} data-testid={`send-draft-${d.id}`} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                      <Send className="w-3 h-3" /> Approve & send
                    </button>
                    <button onClick={() => openEdit(d)} data-testid={`edit-draft-${d.id}`} className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1">
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                  </div>
                )}
                {d.status === "sent" && (
                  <div className="mt-3 flex items-center gap-1 text-[#008A27] text-xs font-bold uppercase tracking-widest"><Check className="w-3 h-3" /> Delivered · {new Date(d.sent_at).toLocaleString()}</div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
